import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Import auth components
import SignIn from './auth/SignIn';
import UserTypeSelection from './auth/UserTypeSelection';
import ClientSignUp from './auth/ClientSignUp';
import ProfessionalSignUp from './auth/ProfessionalSignUp';
import EmailVerification from './auth/EmailVerification';

const Auth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Main flow state
  const [mode, setMode] = useState(searchParams.get('mode') === 'signup' ? 'signup' : 'signin');
  const [userType, setUserType] = useState(''); // 'client' or 'professional'
  const [currentStep, setCurrentStep] = useState(0);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    // If user is already logged in, redirect to appropriate dashboard
    if (currentUser && currentUser.emailVerified) {
      navigate('/my-dashboard');
    }
    
    // If user is logged in but email not verified, show verification
    if (currentUser && !currentUser.emailVerified) {
      setShowEmailVerification(true);
      setUserEmail(currentUser.email);
    }
  }, [currentUser, navigate]);

  // Handle switching between signin and signup
  const switchToSignUp = () => {
    setMode('signup');
    setCurrentStep(0);
    setUserType('');
  };

  const switchToSignIn = () => {
    setMode('signin');
    setCurrentStep(0);
    setUserType('');
  };

  // Handle user type selection
  const handleUserTypeNext = () => {
    setCurrentStep(1);
  };

  const handleBackToUserType = () => {
    setCurrentStep(0);
    setUserType('');
  };

  // Handle successful registration (show email verification)
  const handleRegistrationSuccess = (email, type) => {
    setUserEmail(email);
    setUserType(type);
    setShowEmailVerification(true);
  };

  // Show email verification if needed
  if (showEmailVerification) {
    return <EmailVerification userEmail={userEmail} userType={userType} />;
  }

  // Sign In Mode
  if (mode === 'signin') {
    return <SignIn onSwitchToSignUp={switchToSignUp} />;
  }

  // Sign Up Mode
  if (mode === 'signup') {
    // Step 0: User type selection
    if (currentStep === 0) {
      return (
        <UserTypeSelection
          userType={userType}
          setUserType={setUserType}
          onNext={handleUserTypeNext}
          onSwitchToSignIn={switchToSignIn}
        />
      );
    }

    // Step 1: Registration forms
    if (currentStep === 1) {
      if (userType === 'client') {
        return <ClientSignUp onBack={handleBackToUserType} />;
      }
      
      if (userType === 'professional') {
        return <ProfessionalSignUp onBack={handleBackToUserType} />;
      }
    }
  }

  // Fallback - should not reach here
  return <SignIn onSwitchToSignUp={switchToSignUp} />;
};

export default Auth;